import React, { useEffect, useState, useRef, useCallback } from 'react';
import socketService from '../services/SocketService';
import UploadService from '../services/UploadService';
import '../styles/SoundPlayer.css';

/**
 * Composant SoundPlayer - À utiliser comme source navigateur dans OBS
 *
 * Ce composant écoute les événements Socket.io pour jouer les sons
 * associés aux commandes personnalisées.
 *
 * URL à utiliser dans OBS: http://localhost:3000/sound-player
 * (ou l'URL de votre frontend + /sound-player)
 */
export default function SoundPlayer({ standalone = false, playSound = true }) {
  const [connected, setConnected] = useState(false);
  const [lastSound, setLastSound] = useState(null);
  const [soundQueue, setSoundQueue] = useState([]);
  const [isPlaying, setIsPlaying] = useState(false);
  // En mode standalone (OBS), on active l'audio automatiquement car OBS gère l'audio différemment
  // playSound contrôle si le composant doit jouer les sons ou juste afficher l'état
  const [audioEnabled, setAudioEnabled] = useState(standalone && playSound);
  const audioRef = useRef(null);

  // Si playSound est false, on ne joue pas les sons (utile pour le dashboard)
  const shouldPlaySound = standalone && playSound;

  // Fonction pour activer l'audio (nécessaire pour débloquer la lecture automatique)
  const enableAudio = useCallback(async () => {
    try {
      // Méthode 1: Utiliser AudioContext (plus moderne)
      const ctx = new (window.AudioContext || window.webkitAudioContext)();

      // Créer un buffer silencieux très court (0.1 seconde)
      const buffer = ctx.createBuffer(1, ctx.sampleRate * 0.1, ctx.sampleRate);
      const source = ctx.createBufferSource();
      source.buffer = buffer;
      source.connect(ctx.destination);
      source.start(0);
      source.stop(ctx.currentTime + 0.1);

      // Méthode 2: Créer un élément Audio et le jouer (plus compatible)
      // Utiliser un Data URI pour un son silencieux WAV
      // WAV silencieux de 0.1 seconde (44.1kHz, mono, 16-bit)
      const silentWav =
        'data:audio/wav;base64,UklGRigAAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQAAAAA=';
      const testAudio = new Audio(silentWav);
      testAudio.volume = 0.01; // Volume très faible

      // Jouer le son silencieux pour débloquer l'audio
      await testAudio.play();
      testAudio.pause();
      testAudio.currentTime = 0;

      setAudioEnabled(true);
      console.log(
        '✅ Audio activé - La lecture automatique est maintenant possible'
      );
    } catch (error) {
      console.error("❌ Erreur lors de l'activation de l'audio:", error);
      // Même en cas d'erreur, on active quand même pour permettre les tentatives
      setAudioEnabled(true);
    }
  }, []);

  // Fonction pour jouer le prochain son dans la queue
  const playNextSound = useCallback(() => {
    // Si on ne doit pas jouer les sons (mode dashboard), ne rien faire
    if (!shouldPlaySound) {
      return;
    }

    // Si un son est déjà en cours de lecture, ne pas en démarrer un autre
    if (isPlaying) {
      console.log('⏸️ Un son est déjà en cours de lecture, attente...');
      return;
    }

    setSoundQueue((prevQueue) => {
      if (prevQueue.length === 0) {
        setIsPlaying(false);
        return prevQueue;
      }

      const [nextSound, ...remainingQueue] = prevQueue;
      setLastSound(nextSound);
      setIsPlaying(true);

      // Nettoyer l'ancien audio s'il existe
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      // Créer et jouer l'audio
      // Utiliser l'URL complète pour les fichiers uploadés localement
      const fullSoundUrl = UploadService.getFullUrl(nextSound.soundUrl);
      console.log('🎵 Tentative de lecture du son:', {
        originalUrl: nextSound.soundUrl,
        fullUrl: fullSoundUrl,
        volume: nextSound.soundVolume || 0.5,
        command: nextSound.commandName,
      });

      const audio = new Audio(fullSoundUrl);
      audio.volume = nextSound.soundVolume || 0.5;
      audioRef.current = audio;

      // Attendre que l'audio soit chargé avant de jouer
      const handleCanPlay = () => {
        console.log('✅ Audio prêt à être joué');
        audio
          .play()
          .then(() => {
            console.log('✅ Lecture du son démarrée');
          })
          .catch((error) => {
            console.error('❌ Impossible de jouer le son:', error);
            console.error('   URL:', fullSoundUrl);
            console.error('   Erreur:', error.message);
            setIsPlaying(false);
            // Passer au son suivant après un délai
            setTimeout(() => {
              playNextSound();
            }, 300);
          });
        audio.removeEventListener('canplay', handleCanPlay);
      };

      audio.addEventListener('canplay', handleCanPlay);

      audio.onended = () => {
        console.log('✅ Son terminé');
        setIsPlaying(false);
        audioRef.current = null;
        // Jouer le prochain son après un court délai
        setTimeout(() => {
          playNextSound();
        }, 300);
      };

      audio.onerror = (error) => {
        console.error('❌ Erreur lors de la lecture du son:', error);
        console.error('   URL:', fullSoundUrl);
        console.error('   Erreur détaillée:', audio.error);
        setIsPlaying(false);
        audioRef.current = null;
        // Passer au son suivant même en cas d'erreur
        setTimeout(() => {
          playNextSound();
        }, 300);
      };

      // Charger l'audio
      audio.load();

      return remainingQueue;
    });
  }, [isPlaying, shouldPlaySound]);

  // Gestionnaire pour les événements de son
  const handlePlaySound = useCallback(
    (data) => {
      console.log('🔊 Événement de son reçu:', data);

      const soundData = {
        commandName: data.commandName,
        soundUrl: data.soundUrl,
        soundVolume: data.soundVolume || 0.5,
        username: data.username,
        timestamp: data.timestamp || new Date().toISOString(),
      };

      setSoundQueue((prevQueue) => {
        const newQueue = [...prevQueue, soundData];

        // Si aucun son n'est en cours de lecture, démarrer la lecture
        if (!isPlaying && prevQueue.length === 0) {
          setTimeout(() => playNextSound(), 0);
        }

        return newQueue;
      });
    },
    [isPlaying, playNextSound]
  );

  // Tentative d'activation automatique de l'audio au chargement (pour OBS)
  useEffect(() => {
    if (standalone && playSound && !audioEnabled) {
      // Essayer d'activer l'audio automatiquement après un court délai
      // Cela peut fonctionner dans OBS si la source a été "interagie" précédemment
      const timer = setTimeout(() => {
        enableAudio().catch(() => {
          console.log('⚠️ Activation automatique échouée, clic utilisateur requis');
        });
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [standalone, playSound, audioEnabled, enableAudio]);

  // Connexion Socket.io
  useEffect(() => {
    // Se connecter au namespace sounds (public, sans authentification)
    // Ce namespace est conçu pour être utilisé dans OBS
    const socket = socketService.connectToSounds();

    socket.on('connect', () => {
      console.log(
        '✅ SoundPlayer connecté au serveur Socket.io (namespace /sounds)'
      );
      setConnected(true);
    });

    socket.on('disconnect', () => {
      console.log('❌ SoundPlayer déconnecté du serveur Socket.io');
      setConnected(false);
    });

    // Écouter l'événement de son de commande
    socket.on('play_command_sound', handlePlaySound);

    return () => {
      socket.off('play_command_sound', handlePlaySound);
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [handlePlaySound]);

  // Si c'est une page standalone (pour OBS), afficher une interface minimale
  if (standalone) {
    // Si l'audio n'est pas encore activé, afficher un bouton pour l'activer
    // OBS nécessite une interaction utilisateur pour débloquer l'autoplay
    if (!audioEnabled) {
      return (
        <div 
          className="soundPlayerStandalone soundPlayerActivate"
          onClick={enableAudio}
          style={{
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '60px',
            padding: '10px',
            backgroundColor: 'rgba(29, 185, 84, 0.1)',
            border: '2px dashed #1db954',
            borderRadius: '8px',
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '8px' }}>🔊</div>
          <div style={{ fontSize: '14px', color: '#1db954', fontWeight: 'bold' }}>
            Cliquez ici pour activer l'audio
          </div>
          <div style={{ fontSize: '10px', color: '#888', marginTop: '4px' }}>
            (requis une seule fois)
          </div>
        </div>
      );
    }

    return (
      <div className="soundPlayerStandalone">
        <div
          className={`statusIndicator ${
            connected ? 'connected' : 'disconnected'
          }`}
        >
          {connected ? '🟢' : '🔴'}
        </div>
        {connected && (
          <div style={{ fontSize: '10px', color: '#1db954', marginTop: '4px' }}>
            ✅ Prêt
          </div>
        )}
        {lastSound && (
          <div className="lastSoundInfo">
            <span className="commandName">!{lastSound.commandName}</span>
            <span className="username">par {lastSound.username}</span>
          </div>
        )}
        {isPlaying && <div className="playingIndicator">🔊</div>}
      </div>
    );
  }

  // Interface complète pour le dashboard
  return (
    <div className="soundPlayerContainer">
      <div className="soundPlayerHeader">
        <h3 className="soundPlayerTitle">🔊 Lecteur de Sons</h3>
        <div
          className={`connectionStatus ${
            connected ? 'connected' : 'disconnected'
          }`}
        >
          {connected ? 'Connecté' : 'Déconnecté'}
        </div>
      </div>

      <div className="soundPlayerInfo">
        <p>
          Ce composant joue automatiquement les sons associés aux commandes
          personnalisées lorsqu'elles sont utilisées dans le chat.
        </p>
        <p>
          <strong>Pour OBS :</strong> Ajoutez une source « Navigateur » avec l'URL
          :
        </p>
        <code className="obsUrl">{window.location.origin}/sound-player</code>
        <p className="obsTip">
          Si aucun son : rafraîchissez la source (F5), puis clic droit sur la source → Propriétés →
          cochez « Contrôle audio via OBS » (ou « Shutdown source when not visible » désactivé).
        </p>
      </div>

      {lastSound && (
        <div className="lastSoundCard">
          <h4>Dernier son joué</h4>
          <div className="soundDetails">
            <span className="commandName">!{lastSound.commandName}</span>
            <span className="username">par {lastSound.username}</span>
            <span className="timestamp">
              {new Date(lastSound.timestamp).toLocaleTimeString()}
            </span>
          </div>
        </div>
      )}

      {soundQueue.length > 0 && (
        <div className="queueInfo">
          <span>{soundQueue.length} son(s) en attente</span>
        </div>
      )}

      {isPlaying && (
        <div className="playingStatus">
          <span className="playingIcon">🔊</span>
          <span>Lecture en cours...</span>
        </div>
      )}
    </div>
  );
}
